// ---------------------------------------------------------------------------
// Single place that dispatches "build one AEMET-acquired series" by
// acquisition method ("rocio"/"idw"/"nearest") — used identically by a real
// fetch (aemetJobStore.ts's applyResult) and a cold retry (aemetRetry.ts).
// The exact same 3-way method dispatch used to be written out by hand 3
// times (precip/temp/eto) in EACH of those two files — 6 copies total, with
// real drift already causing one bug between them (aemetRetry.ts's own
// header comment records it: a plain `!isRocio` check there used to always
// take the nearest-station path even for an "idw" provenance, silently
// mixing aggregation methods within the same column on retry). Merged here
// so both call sites share one dispatch instead of two copies that can
// individually go stale.
// ---------------------------------------------------------------------------
import type { AemetDailyRecord, StationSelectionMethod } from "./aemet/aemet.ts";
import {
  buildEtoSeries,
  buildEtoSeriesIdw,
  buildSeriesFromStations,
  buildSeriesFromStationsIdw,
  type BuiltSeries,
} from "./aemet/aemet.ts";
import { buildRocioEtoSeries, buildRocioSeries } from "./rocio.ts";

/** Which of the 3 input series this call is building — each has its own ROCIO aggregation op and station variable name (see the body below). */
export type SeriesKind = "precip" | "temp" | "eto";

/** Only the fields IDW weighting actually reads from a candidate — same shape `buildSeriesFromStationsIdw`/`buildEtoSeriesIdw` (aemet.ts) already expect, kept structural here (no import of aemet.ts's own non-exported `IdwCandidate`) so both `AemetJob.candidates` and `AemetProvenance.stationsUsed` (storage.ts) can be passed as-is. */
interface IdwLikeCandidate {
  code: string;
  distanceKm: number;
}

/**
 * Builds one series (precip/temp/eto) for whichever `method` produced the
 * fetch — "rocio" aggregates every candidate cell by mean/min/max instead of
 * blending by distance (see rocio.ts's module header comment for why);
 * "idw" blends every candidate weighted by 1/distance²; anything else falls
 * back to nearest-candidate-with-data. `codes`/`candidates` overlap in
 * content (both ultimately come from the same candidate list) but have
 * different shapes because "rocio"/nearest only need the id/code, while
 * "idw" also needs `distanceKm` — same split the two original call sites
 * already had, just no longer duplicated.
 */
export function buildSeriesForMethod(
  method: StationSelectionMethod,
  kind: SeriesKind,
  codes: string[],
  candidates: IdwLikeCandidate[],
  byStationByDate: Map<string, Map<string, AemetDailyRecord>>,
  dates: string[],
  latDeg: number
): BuiltSeries {
  if (method === "rocio") {
    switch (kind) {
      case "precip":
        return buildRocioSeries(codes, byStationByDate, dates, "prec", "mean");
      // "rocio" temperature uses the MINIMUM across cells — an explicit
      // product decision, not an approximation (see aemetDialog.methodRocioHelp).
      case "temp":
        return buildRocioSeries(codes, byStationByDate, dates, "tmin", "min");
      case "eto":
        return buildRocioEtoSeries(codes, byStationByDate, dates, latDeg);
    }
  }
  if (method === "idw") {
    switch (kind) {
      case "precip":
        return buildSeriesFromStationsIdw(candidates, byStationByDate, dates, "prec");
      case "temp":
        return buildSeriesFromStationsIdw(candidates, byStationByDate, dates, "tmed");
      case "eto":
        return buildEtoSeriesIdw(candidates, byStationByDate, dates, latDeg);
    }
  }
  switch (kind) {
    case "precip":
      return buildSeriesFromStations(codes, byStationByDate, dates, "prec");
    case "temp":
      return buildSeriesFromStations(codes, byStationByDate, dates, "tmed");
    case "eto":
      return buildEtoSeries(codes, byStationByDate, dates, latDeg);
  }
}
